/**
 * Brand configuration types, defaults, and presets.
 * Mirrors the brand_config DB table schema (supabase/migrations/20260318000009_brand_config.sql).
 */

/**
 * Available storefront layout themes.
 */
export type ThemeName =
  | 'classic' | 'boutique' | 'modern'
  | 'fashion-minimal' | 'fashion-bold' | 'fashion-luxury'
  | 'electronics-tech' | 'food-grocery' | 'beauty-cosmetics'
  | 'furniture-home' | 'kids-toys' | 'jewelry'
  | 'sports' | 'books-stationery'
  | 'general-a' | 'general-b';

export const THEME_NAMES: ThemeName[] = [
  'classic', 'boutique', 'modern',
  'fashion-minimal', 'fashion-bold', 'fashion-luxury',
  'electronics-tech', 'food-grocery', 'beauty-cosmetics',
  'furniture-home', 'kids-toys', 'jewelry',
  'sports', 'books-stationery',
  'general-a', 'general-b',
];

/**
 * Shape of a brand_config row from the database.
 */
export type NavLinkItem = {
  id: string;
  label_ar: string;
  label_en: string;
  url: string;
  type: 'page' | 'category' | 'external' | 'custom';
  open_new_tab?: boolean;
  children?: NavLinkItem[];
};

export type SocialPlatform =
  | 'facebook' | 'instagram' | 'x' | 'tiktok' | 'youtube'
  | 'linkedin' | 'pinterest' | 'snapchat' | 'telegram'
  | 'whatsapp' | 'threads' | 'discord' | 'twitch'
  | 'reddit' | 'github' | 'behance' | 'dribbble'
  | 'medium' | 'spotify';

export type SocialLinkItem = {
  id: string;
  platform: SocialPlatform;
  username: string;
};

export const SOCIAL_PLATFORMS: Record<SocialPlatform, {
  label_en: string;
  label_ar: string;
  prefix: string;
  placeholder: string;
  icon: string; // Phosphor icon name
}> = {
  facebook:  { label_en: 'Facebook',  label_ar: 'فيسبوك',   prefix: 'https://facebook.com/',   placeholder: 'username', icon: 'FacebookLogo' },
  instagram: { label_en: 'Instagram', label_ar: 'إنستغرام', prefix: 'https://instagram.com/',  placeholder: 'username', icon: 'InstagramLogo' },
  x:         { label_en: 'X',         label_ar: 'إكس',      prefix: 'https://x.com/',           placeholder: 'username', icon: 'XLogo' },
  tiktok:    { label_en: 'TikTok',    label_ar: 'تيك توك',  prefix: 'https://tiktok.com/@',     placeholder: 'username', icon: 'TiktokLogo' },
  youtube:   { label_en: 'YouTube',   label_ar: 'يوتيوب',   prefix: 'https://youtube.com/@',    placeholder: 'channel',  icon: 'YoutubeLogo' },
  linkedin:  { label_en: 'LinkedIn',  label_ar: 'لينكدإن',  prefix: 'https://linkedin.com/in/', placeholder: 'username', icon: 'LinkedinLogo' },
  pinterest: { label_en: 'Pinterest', label_ar: 'بنترست',   prefix: 'https://pinterest.com/',   placeholder: 'username', icon: 'PinterestLogo' },
  snapchat:  { label_en: 'Snapchat',  label_ar: 'سناب شات', prefix: 'https://snapchat.com/add/',placeholder: 'username', icon: 'SnapchatLogo' },
  telegram:  { label_en: 'Telegram',  label_ar: 'تليجرام',  prefix: 'https://t.me/',            placeholder: 'username', icon: 'TelegramLogo' },
  whatsapp:  { label_en: 'WhatsApp',  label_ar: 'واتس آب',  prefix: 'https://wa.me/',           placeholder: 'phone',    icon: 'WhatsappLogo' },
  threads:   { label_en: 'Threads',   label_ar: 'ثريدز',    prefix: 'https://threads.net/@',    placeholder: 'username', icon: 'ThreadsLogo' },
  discord:   { label_en: 'Discord',   label_ar: 'ديسكورد',  prefix: 'https://discord.gg/',      placeholder: 'invite',   icon: 'DiscordLogo' },
  twitch:    { label_en: 'Twitch',    label_ar: 'تويتش',    prefix: 'https://twitch.tv/',       placeholder: 'username', icon: 'TwitchLogo' },
  reddit:    { label_en: 'Reddit',    label_ar: 'ريديت',    prefix: 'https://reddit.com/r/',    placeholder: 'subreddit',icon: 'RedditLogo' },
  github:    { label_en: 'GitHub',    label_ar: 'غيت هاب',  prefix: 'https://github.com/',      placeholder: 'username', icon: 'GithubLogo' },
  behance:   { label_en: 'Behance',   label_ar: 'بيهانس',   prefix: 'https://behance.net/',     placeholder: 'username', icon: 'BehanceLogo' },
  dribbble:  { label_en: 'Dribbble',  label_ar: 'دريبل',    prefix: 'https://dribbble.com/',    placeholder: 'username', icon: 'DribbbleLogo' },
  medium:    { label_en: 'Medium',    label_ar: 'ميديوم',   prefix: 'https://medium.com/@',     placeholder: 'username', icon: 'MediumLogo' },
  spotify:   { label_en: 'Spotify',   label_ar: 'سبوتيفاي', prefix: 'https://spotify.com/',     placeholder: 'profile',  icon: 'SpotifyLogo' },
};

/**
 * Configurable language for primary/secondary tenant languages.
 * The database fields (_ar / _en) never change — only the display name,
 * code, and direction of the secondary language are configurable.
 */
export interface LanguageConfig {
  code: string;       // e.g. "he", "fr", "en", "tr"
  name: string;       // display name e.g. "עברית", "Français"
  direction: 'rtl' | 'ltr';
}

export interface TenantLanguages {
  primary: LanguageConfig;
  secondary: LanguageConfig;
}

export const DEFAULT_LANGUAGES: TenantLanguages = {
  primary: { code: 'ar', name: 'العربية', direction: 'rtl' },
  secondary: { code: 'en', name: 'English', direction: 'ltr' },
};

export type HeaderDesign = 'default' | 'centered' | 'minimal';
export type FooterDesign = 'default' | 'minimal' | 'centered';

export interface TrackingPixels {
  meta_pixel_id?: string;
  google_tag_id?: string;
  tiktok_pixel_id?: string;
  snapchat_pixel_id?: string;
  pinterest_tag_id?: string;
}

/** How the storefront treats products that are out of stock. */
export type OutOfStockDisplay = 'show_sold_out' | 'hide';

/**
 * How stock/availability is shown on the storefront:
 *  - 'badge'  — availability badge: "Out of stock" + "Only N left" (default)
 *  - 'exact'  — show the exact remaining count: "N in stock" + "Out of stock"
 *  - 'hide'   — show no stock indicators at all
 */
export type StockDisplay = 'badge' | 'exact' | 'hide';

export type CategoriesIndexLevel = 'auto' | 'top' | 'second';

export type BottomBarTab = 'categories' | 'products';

/** Catalog-wide product defaults (admin → Settings → Products). */
export interface ProductSettings {
  /** Stock at or below this is flagged "low stock" in admin inventory. */
  low_stock_threshold?: number;
  /** How stock/availability is shown on the storefront. */
  stock_display?: StockDisplay;
  /** Default minimum order quantity prefilled on new products. */
  default_min_order_qty?: number;
  /** Default quantity step prefilled on new products. */
  default_qty_step?: number;
  /** Whether new products track inventory by default. */
  track_inventory_by_default?: boolean;
  /** Storefront behavior for out-of-stock products. */
  out_of_stock_display?: OutOfStockDisplay;
  /** Status applied to newly created products. */
  default_product_status?: 'draft' | 'active';
  /**
   * Store-wide kill switch for the wishlist. When false the favourite control
   * is gone everywhere — PDP, product cards, quick view — regardless of any
   * per-widget `show_wishlist` the page builder has set. When true the widget
   * toggle decides per section, as before.
   */
  wishlist_enabled?: boolean;
  /**
   * Which categories `/categories` lists. `auto` (default) shows the top
   * level, replacing a top-level group that has no products of its own by its
   * subcategories; `top` always shows the top level; `second` shows the
   * subcategories of every top-level category.
   */
  categories_index_level?: CategoriesIndexLevel;
  /**
   * The mobile bottom bar's second tab. `categories` (default) falls back to
   * all products when the store has no categories.
   */
  bottom_bar_tab?: BottomBarTab;
  /**
   * Store-wide customer note shown under the description on every product
   * page. Resolved at render time, not copied onto products: a product with an
   * empty `note_ar`/`note_en` inherits this, a product with its own note
   * overrides it, and a product with `hide_default_note` shows nothing.
   */
  default_note_ar?: string;
  default_note_en?: string;
}

/** Checkout behaviour toggles (admin → Settings → General → Checkout). */
export interface OrdersSettings {
  /**
   * Where order numbering starts. Applied as a floor by the
   * `assign_order_number` trigger, so raising it jumps the sequence forward
   * and lowering it is inert — numbers can never repeat.
   */
  number_start?: number;
}

export interface CheckoutSettings {
  /**
   * Whether shoppers can see and redeem gift cards anywhere on the storefront.
   * Absent means enabled — existing stores keep today's behaviour.
   */
  gift_cards_enabled?: boolean;
}

export interface PromoSettings {
  enabled?: boolean;
  /** Regenerated when the promo is switched off -> on. Keys the dismissal. */
  campaign_id?: string;
  /** 'image' = the image is the whole dialog. 'composed' = image + text + CTA. */
  mode?: 'image' | 'composed';

  image_url?: string;
  /** Required once an image is set — in image mode the image IS the content. */
  image_alt_ar?: string;
  image_alt_en?: string;

  /** composed mode only */
  heading_ar?: string;
  heading_en?: string;
  body_ar?: string;
  body_en?: string;
  button_label_ar?: string;
  button_label_en?: string;

  /** Button target in composed mode; whole-image target in image mode. */
  link_url?: string;

  /** Optional window. ISO 8601. Either bound may be omitted. */
  starts_at?: string;
  ends_at?: string;
}

export type ListingCard = "glass-spec";

/** The listing card a store opted into, or undefined for the standard card. */
export function resolveListingCard(
  settings: StoreSettings | null | undefined,
): ListingCard | undefined {
  return settings?.appearance?.listing_card === "glass-spec" ? "glass-spec" : undefined;
}

export interface StoreSettings {
  /** Storefront look. `scheme` is the store default (light, dark, or follow the device); setting it also ships both palettes so shoppers can toggle. */
  appearance?: {
    scheme?: "light" | "dark" | "system";
    font_display?: string;
    /** Listing-grid card. Absent = the standard card; "glass-spec" = the homepage glass card. */
    listing_card?: ListingCard;
  };
  /** Configurable primary/secondary languages for this tenant */
  languages?: {
    primary?: Partial<LanguageConfig>;
    secondary?: Partial<LanguageConfig>;
  };
  tracking?: TrackingPixels;
  /** Which fulfilment methods the store offers at checkout. */
  fulfilment?: {
    delivery_enabled?: boolean;
    pickup_enabled?: boolean;
  };
  /** Catalog-wide product defaults. */
  products?: ProductSettings;
  /** Checkout behaviour toggles. */
  checkout?: CheckoutSettings;
  /** Order numbering. */
  orders?: OrdersSettings;
  /** Promotional modal shown once on the home page. */
  promo?: PromoSettings;
  phone?: string;
  whatsapp?: string;
  email?: string;
  address?: string;
  /** @deprecated Use social_links instead */
  social?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    tiktok?: string;
  };
  social_links?: SocialLinkItem[];
  nav_links?: NavLinkItem[];
  footer_links?: NavLinkItem[];
  header?: {
    design?: HeaderDesign;
    nav_position?: 'inline' | 'below';
    nav_bg_color?: string;
    nav_text_color?: string;
    show_categories_nav?: boolean;
    show_search?: boolean;
    show_currency_switcher?: boolean;
    show_locale_switcher?: boolean;
    show_wishlist?: boolean;
    show_auth?: boolean;
    bg_color?: string;
    text_color?: string;
    sticky?: boolean;
    announcement_ar?: string;
    announcement_en?: string;
  };
  footer?: {
    design?: FooterDesign;
    show_newsletter?: boolean;
    show_social?: boolean;
    show_contact?: boolean;
    show_quick_links?: boolean;
    bg_color?: string;
    text_color?: string;
    description_ar?: string;
    description_en?: string;
    copyright_ar?: string;
    copyright_en?: string;
  };
}

export interface BrandConfig {
  id: number;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  store_name: string;
  font_arabic: string;
  font_ltr: string;
  theme: ThemeName;
  store_settings: StoreSettings;
  updated_at: string;
}

/**
 * Default brand values — Teal & Gold, matching the current Uqhoan globals.css theme.
 * Used as fallback when brand_config row is unavailable or not yet seeded.
 */
export const DEFAULT_BRAND: Omit<BrandConfig, 'id' | 'updated_at'> = {
  primary_color: '#2bbfb0',
  secondary_color: '#f5c300',
  logo_url: null,
  store_name: 'Uqhoan',
  font_arabic: 'IBM Plex Sans Arabic',
  font_ltr: 'Geist',
  theme: 'classic',
  store_settings: {},
};

/**
 * Shape of a brand preset option in the admin panel.
 */
export interface BrandPreset {
  id: string;
  /** Translation key in `AdminTranslations`. Resolved at render time. */
  labelKey: 'colorPresetTealGold' | 'colorPresetRoseGold' | 'colorPresetLuxePurple';
  primary: string;
  secondary: string;
}

/**
 * Curated color presets available in the brand admin panel.
 * The first preset (teal-gold) matches DEFAULT_BRAND and is the factory default.
 */
export const PRESETS: BrandPreset[] = [
  {
    id: 'teal-gold',
    labelKey: 'colorPresetTealGold',
    primary: '#2bbfb0',
    secondary: '#f5c300',
  },
  {
    id: 'rose-amber',
    labelKey: 'colorPresetRoseGold',
    primary: '#c0546a',
    secondary: '#d4a853',
  },
  {
    id: 'purple-gold',
    labelKey: 'colorPresetLuxePurple',
    primary: '#6b5ca5',
    secondary: '#f0a500',
  },
];
